import React from "react";
import { graphql, useFragment } from "react-relay";

import { faRocket, faSeedling, faUsersClass } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserFriends } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import type { DistributiveOmit } from "~/types/main.d";
import { colors } from "~/ui/colors";
import Avatar, { type UserAttrs } from "~/ui/legacy/Avatar";

import fellowLogo from "./assets/fellow-icon.svg?url";

import type {
	TemplateAuthor_Author$key as AuthorFragment,
	TemplateAuthor_Author$data,
} from "~/__generated__/TemplateAuthor_Author.graphql";

import styles from "./css/TemplateAuthor.module.css";

const icons = {
	seedling: faSeedling,
	rocket: faRocket,
	"users-class": faUsersClass,
	"user-friends": faUserFriends,
};

interface UserAuthorProps {
	user: UserAttrs;
	logoOnly: boolean;
}

interface CompanyAuthorProps {
	/** Something suitable for using as <img src={...} /> */
	logo: string;
	darkModeLogo: string | null;
	name: string;
	logoOnly: boolean;
}

interface WorkspaceAuthorProps {
	/** Something suitable for using as <img src={...} /> */
	logo: string;
	name: string;
	squareImage?: boolean;
	logoOnly: boolean;
}

interface IconAuthorProps {
	/** If missing, faUsersClass will be used */
	icon?: IconDefinition;
	/** Only the icon will be shown if no name is passed */
	name?: string;
	/** If missing, grey800 will be used */
	colour?: string;
	logoOnly: boolean;
}

export function DumbTemplateAuthor({
	author,
	logoOnly = false,
}: {
	author?: DistributiveOmit<TemplateAuthor_Author$data, " $fragmentType"> | null;
	logoOnly?: boolean;
}) {
	if (!author)
		return <TemplateAuthor type="workspace" name="Fellow" logo={fellowLogo} squareImage logoOnly={logoOnly} />;
	switch (author.__typename) {
		case "User": {
			return <TemplateAuthor type="user" user={author} logoOnly={logoOnly} />;
		}
		case "ExternalAccount":
		case "Account": {
			const { __typename, ...rest } = author;
			return <TemplateAuthor type="workspace" logoOnly={logoOnly} {...rest} />;
		}
		case "TemplateUserAuthor": {
			const { __typename, ...rest } = author;
			const user = { ...rest, id: "" };
			return <TemplateAuthor type="user" user={user} logoOnly={logoOnly} />;
		}
		case "TemplateCompanyAuthor": {
			const { __typename, ...rest } = author;
			return <TemplateAuthor type="company" logoOnly={logoOnly} {...rest} />;
		}
		case "TemplateIconAuthor": {
			const { __typename, ...rest } = author;
			const props: IconAuthorProps = { logoOnly: logoOnly };
			if (rest.color) props.colour = rest.color;
			if (rest.icon && rest.icon in icons) props.icon = icons[rest.icon as keyof typeof icons];
			if (rest.nameThatIsOptional) props.name = rest.nameThatIsOptional;
			return <TemplateAuthor type="icon" {...props} />;
		}
		default:
			return <TemplateAuthor type="icon" logoOnly />;
	}
}

export function SmartTemplateAuthor({
	author: authorKey,
	logoOnly = false,
}: {
	author: AuthorFragment | null;
	logoOnly?: boolean;
}) {
	const author = useFragment(
		graphql`
			fragment TemplateAuthor_Author on NoteTemplateAuthor {
				__typename

				... on User {
					id
					fullName
					image
				}
				... on Account {
					name
					logo
				}
				... on ExternalAccount {
					name
					logo
				}
				... on TemplateUserAuthor {
					image
					fullName: name
				}
				... on TemplateCompanyAuthor {
					name
					logo
					darkModeLogo
				}
				... on TemplateIconAuthor {
					icon
					color
					nameThatIsOptional: name
				}
			}
		`,
		authorKey,
	);

	return <DumbTemplateAuthor author={author} logoOnly={logoOnly} />;
}

type TemplateAuthorProps =
	| ({ type: "user" } & UserAuthorProps)
	| ({ type: "company" } & CompanyAuthorProps)
	| ({ type: "workspace" } & WorkspaceAuthorProps)
	| ({ type: "icon" } & IconAuthorProps);

export function TemplateAuthor(props: TemplateAuthorProps) {
	switch (props.type) {
		case "icon": {
			const { type: _, ...rest } = props;
			return <IconAuthor {...rest} />;
		}
		case "company": {
			const { type: _, ...rest } = props;
			return <CompanyAuthor {...rest} />;
		}
		case "workspace": {
			const { type: _, ...rest } = props;
			return <WorkspaceAuthor {...rest} />;
		}
		case "user": {
			const { type: _, ...rest } = props;
			return <UserAuthor {...rest} />;
		}
	}
}

function BaseAuthor({ logo, name, logoOnly }: { logo: React.ReactNode; name?: string; logoOnly: boolean }) {
	if (logoOnly) {
		return <div className={styles.logoSmall}>{logo}</div>;
	}

	return (
		<div className={styles.author}>
			<div className={styles.logo}>{logo}</div>
			{name !== undefined && <span className={styles.name}>{name}</span>}
		</div>
	);
}

function IconSvg({ icon, colour }: { icon: IconDefinition; colour: string }) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={styles.iconCircle}>
			<circle cx="12" cy="12" r="12" fill={colour} />
			<FontAwesomeIcon icon={icon} fixedWidth className={styles.icon} width="14" x="5" y="0" />
		</svg>
	);
}

function IconAuthor({ icon, colour, name, logoOnly }: IconAuthorProps) {
	return (
		<BaseAuthor
			logo={<IconSvg icon={icon ?? faUsersClass} colour={colour ?? colors.grey[800]} />}
			name={name ?? ""}
			logoOnly={logoOnly}
		/>
	);
}

function Image({ circle, square, src, alt }: { circle?: boolean; square?: boolean; src: string; alt: string }) {
	return <img alt={alt} src={src} className={cx(styles.image, { [styles.circle]: circle, [styles.square]: square })} />;
}

function CompanyAuthor({ logo, darkModeLogo, name, logoOnly }: CompanyAuthorProps) {
	const src = (document.documentElement.dataset.theme === "theme-dark" ? darkModeLogo : null) ?? logo;
	return <BaseAuthor logo={<Image alt={name} src={src} />} name={name} logoOnly={logoOnly} />;
}

function WorkspaceAuthor({ logo, name, squareImage, logoOnly }: WorkspaceAuthorProps) {
	return (
		<BaseAuthor logo={<Image square circle={!squareImage} alt={name} src={logo} />} name={name} logoOnly={logoOnly} />
	);
}

function UserAuthor({ user, logoOnly }: UserAuthorProps) {
	return (
		<BaseAuthor logo={<Avatar user={user} circle width="1.5rem" withAlt />} name={user.fullName} logoOnly={logoOnly} />
	);
}
